import {makeAutoObservable} from "mobx";
import {KpiShippingLoadParams} from "./commons";
import {api, convertDataState, convertDocumentType} from "../../api";
import {DataStateDto, DocumentTypeDto, RepositoryListItemDto} from "../../api/kpi";

class KpiRepositoryStore {
    get storedCount(): number {
        return this._storedCount;
    }

    set storedCount(value: number) {
        this._storedCount = value;
    }

    get storedCountByType(): Record<string, unknown>[] {
        return this._storedCountByType;
    }

    set storedCountByType(value: Record<string, unknown>[]) {
        this._storedCountByType = value;
    }

    get lastLoaded(): number {
        return this._lastLoaded;
    }

    set lastLoaded(value: number) {
        this._lastLoaded = value;
    }

    get typeCount(): Record<string, unknown>[] {
        return this._typeCount;
    }

    set typeCount(value: Record<string, unknown>[]) {
        this._typeCount = value;
    }

    get stateCount(): Record<string, unknown>[] {
        return this._stateCount;
    }

    set stateCount(value: Record<string, unknown>[]) {
        this._stateCount = value;
    }

    get total(): number {
        return this._total;
    }

    set total(value: number) {
        this._total = value;
    }

    private _total: number = 0
    private _lastLoaded: number = 0
    private _stateCount: Record<string, unknown>[] = []
    private _typeCount: Record<string, unknown>[] = []
    private _storedCount: number = 0
    private _storedCountByType: Record<string, unknown>[] = []

    constructor() {
        makeAutoObservable(this)
    }

    async load(query: KpiShippingLoadParams) {
        // loading repository data isan expensive operation
        if (Date.now() - 10000 < this.lastLoaded) {
            return
        }
        try {
            const response = await api.kpi.api.listRepositoryEntries(query)
            const items = response.data.items || []
            this.countTotal(items)
            this.countStoredTotal(items)
            this.groupByStatus(items)
            this.groupByType(items)
            this.groupByTypeStatus(items)
            this.lastLoaded = Date.now()
        } catch (ignored) {

        }
    }

    private countTotal(items: RepositoryListItemDto[]) {
        this.total = items.map(v => v.count).reduce((p, c) => {
            return p + c
        }, 0)
    }

    private countStoredTotal(items: RepositoryListItemDto[]) {
        this.storedCount = items
            .filter(e => e.state === DataStateDto.STORED)
            .map(v => v.count)
            .reduce((p, c) => {
                return p + c
            }, 0)
    }

    private groupByStatus(items: RepositoryListItemDto[]) {
        const map = items.reduce((p, current) => {
            p.set(current.state, ((p.get(current.state) || 0) + current.count))
            return p
        }, new Map<DataStateDto, number>())
        this.stateCount = Array.from(map.entries()).map(([state, count]) => ({
            name: convertDataState(state),
            state: state,
            value: count
        }))
    }

    private groupByType(items: RepositoryListItemDto[]) {
        const map = items.reduce((p, current) => {
            p.set(current.type, ((p.get(current.type) || 0) + current.count))
            return p
        }, new Map<DocumentTypeDto, number>())
        this.typeCount = Array.from(map.entries()).map(([type, count]) => ({
            name: convertDocumentType(type),
            type: type,
            value: count
        }))
    }

    private groupByTypeStatus(items: RepositoryListItemDto[]) {
        interface Mem {
            type: DocumentTypeDto
            state: DataStateDto
            v: number
        }

        const map = items
            .filter(e => e.state === DataStateDto.STORED)
            .reduce((p, current) => {
                const key = current.type + ":" + current.state
                const v = (p.get(key)?.v || 0) + current.count
                p.set(key, {
                    v,
                    type: current.type,
                    state: current.state
                })
                return p
            }, new Map<string, Mem>())

        this.storedCountByType = Array.from(map.entries()).map(([, e]) => ({
            name: [convertDocumentType(e.type), convertDataState(e.state)].join(' '),
            type: e.type,
            state: e.state,
            value: e.v
        }))
    }
}

const kpiRepositoryStore = new KpiRepositoryStore()

export {
    kpiRepositoryStore
}
